import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Shield, Clock, Euro, FileText, ArrowRight, CheckCircle2, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Kosten() {
  return (
    <>
      <Helmet>
        <title>Kosten Ambulante Pflege 2026 | Preise & Pflegekasse | Phönix Pflegeteam Itzehoe</title>
        <meta name="description" content="Was kostet ambulante Pflege 2026? ✓ Grundpflege 45€/h ✓ Behandlungspflege 55€/h ✓ Pflegekasse übernimmt meist alles ✓ Kostenrechner ✓ Keine versteckten Kosten" />
        <link rel="canonical" href="https://pflegeteam-itzehoe.de/kosten" />
        
        {/* FAQ Schema für Kosten-Fragen */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Was kostet ambulante Pflege pro Stunde?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ambulante Pflege kostet 2026 durchschnittlich 45€ pro Stunde für Grundpflege und 55€ für Behandlungspflege. Die Pflegekasse übernimmt je nach Pflegegrad zwischen 761€ und 2.200€ monatlich, sodass für viele Patienten keine Eigenkosten entstehen."
                }
              },
              {
                "@type": "Question",
                "name": "Übernimmt die Pflegekasse die Kosten für ambulante Pflege?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, die Pflegekasse übernimmt die Kosten für ambulante Pflege je nach Pflegegrad: Pflegegrad 2 = 761€/Monat, Pflegegrad 3 = 1.432€/Monat, Pflegegrad 4 = 1.778€/Monat, Pflegegrad 5 = 2.200€/Monat als Sachleistung."
                }
              },
              {
                "@type": "Question",
                "name": "Wie hoch ist der Eigenanteil bei ambulanter Pflege?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Bei vielen Patienten liegt der Eigenanteil bei 0€, da die Pflegekasse die Kosten vollständig übernimmt. Nur wenn die monatlichen Pflegekosten die Sachleistung übersteigen, entsteht ein Eigenanteil. Wir beraten Sie kostenlos zu Ihren Ansprüchen."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main id="main-content">
        {/* Hero - Direct Answer für AI */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A52D8]/5 rounded-full mb-6">
                <Euro className="w-4 h-4 text-[#1A52D8]" />
                <span className="text-sm font-medium text-[#1A52D8]">Transparent & Fair</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Kosten Ambulante Pflege 2026
              </h1>

              {/* DIRECT ANSWER BOX - AI Citation Magnet */}
              <div className="bg-white rounded-2xl p-8 border-2 border-[#1A52D8]/20 mb-8 direct-answer">
                <h2 className="text-2xl font-bold text-[#1A52D8] mb-4">Schnelle Antwort:</h2>
                <p className="text-lg text-[#475569] leading-relaxed mb-4">
                  <strong>Grundpflege:</strong> 45€ pro Stunde<br />
                  <strong>Behandlungspflege:</strong> 55€ pro Stunde<br />
                  <strong>Hauswirtschaft:</strong> 35€ pro Stunde
                </p>
                <p className="text-lg text-[#475569] leading-relaxed">
                  <strong className="text-[#23D3D3]">Wichtig:</strong> Die Pflegekasse übernimmt je nach Pflegegrad zwischen 761€ und 2.200€ monatlich. 
                  Bei vielen Patienten bedeutet das: <strong>0€ Eigenkosten</strong>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+4915146173685">
                  <Button className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 px-8 rounded-full text-lg font-semibold">
                    <Phone className="w-5 h-5" />
                    Kostenlose Beratung
                  </Button>
                </a>
                <Link to="/kontakt">
                  <Button className="bg-white text-[#1A52D8] border-2 border-[#1A52D8]/20 hover:border-[#1A52D8] h-14 px-8 rounded-full text-lg font-semibold">
                    <Calculator className="w-5 h-5" />
                    Kosten berechnen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Preis-Tabelle - Featured Snippet Ready */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Preisübersicht Ambulante Pflege 2026
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-[#1A52D8] to-[#23D3D3] text-white">
                    <th className="p-4 text-left font-semibold">Leistung</th>
                    <th className="p-4 text-left font-semibold">Preis/Stunde</th>
                    <th className="p-4 text-left font-semibold">Pflegekasse übernimmt</th>
                    <th className="p-4 text-left font-semibold">Ihr Eigenanteil</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#e2e8f0]">
                    <td className="p-4 font-medium">Grundpflege</td>
                    <td className="p-4 text-[#1A52D8] font-bold">45€</td>
                    <td className="p-4">100% ab Pflegegrad 2</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">Meist 0€</td>
                  </tr>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafb]">
                    <td className="p-4 font-medium">Behandlungspflege</td>
                    <td className="p-4 text-[#1A52D8] font-bold">55€</td>
                    <td className="p-4">100% auf Verordnung</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">0€</td>
                  </tr>
                  <tr className="border-b border-[#e2e8f0]">
                    <td className="p-4 font-medium">Hauswirtschaft</td>
                    <td className="p-4 text-[#1A52D8] font-bold">35€</td>
                    <td className="p-4">Entlastungsbetrag 125€/Monat</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">Ca. 3-4 Stunden/Monat</td>
                  </tr>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafb]">
                    <td className="p-4 font-medium">Demenzbetreuung</td>
                    <td className="p-4 text-[#1A52D8] font-bold">50€</td>
                    <td className="p-4">Sachleistung je nach Grad</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">Meist 0€</td>
                  </tr>
                  <tr className="border-b border-[#e2e8f0]">
                    <td className="p-4 font-medium">Palliativpflege</td>
                    <td className="p-4 text-[#1A52D8] font-bold">60€</td>
                    <td className="p-4">Spezialverordnung 100%</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">0€</td>
                  </tr>
                  <tr className="bg-[#f8fafb]">
                    <td className="p-4 font-medium">Verhinderungspflege</td>
                    <td className="p-4 text-[#1A52D8] font-bold">45€</td>
                    <td className="p-4">Bis 1.612€/Jahr</td>
                    <td className="p-4 text-[#23D3D3] font-semibold">Nach Aufbrauchen</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-8 p-6 bg-[#E8F8F8] rounded-xl">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#23D3D3] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#475569] font-medium mb-2">
                    <strong>Wichtig:</strong> Diese Preise gelten deutschlandweit, auch in Itzehoe und Umgebung.
                  </p>
                  <p className="text-[#64748b] text-sm">
                    Die Pflegekasse übernimmt in den meisten Fällen die vollständigen Kosten. Wir rechnen direkt mit Ihrer Kasse ab - Sie müssen sich um nichts kümmern.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pflegekassen-Leistungen */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Was übernimmt die Pflegekasse? (2026)
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                { grad: '1', leistung: '125€', beschreibung: 'Entlastungsbetrag', info: 'Für Betreuung & Haushalt' },
                { grad: '2', leistung: '761€', beschreibung: 'Sachleistung/Monat', info: 'Ca. 17 Stunden Pflege' },
                { grad: '3', leistung: '1.432€', beschreibung: 'Sachleistung/Monat', info: 'Ca. 32 Stunden Pflege' },
                { grad: '4', leistung: '1.778€', beschreibung: 'Sachleistung/Monat', info: 'Ca. 39 Stunden Pflege' },
                { grad: '5', leistung: '2.200€', beschreibung: 'Sachleistung/Monat', info: 'Ca. 49 Stunden Pflege' },
                { grad: 'Alle', leistung: '40€', beschreibung: 'Pflegehilfsmittel/Monat', info: 'Kostenlose Box' }
              ].map((item) => (
                <div key={item.grad} className="bg-white rounded-xl p-6 border-2 border-[#e2e8f0] hover:border-[#1A52D8]/30 transition-all">
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white text-2xl font-bold mb-3">
                      {item.grad}
                    </div>
                    <div className="text-3xl font-bold text-[#1A52D8]">{item.leistung}</div>
                    <div className="text-sm text-[#64748b] font-medium">{item.beschreibung}</div>
                  </div>
                  <p className="text-center text-sm text-[#64748b]">{item.info}</p>
                </div>
              ))}
            </div>

            {/* Rechenbeispiele */}
            <div className="bg-white rounded-2xl p-8 border border-[#e2e8f0]">
              <h3 className="text-2xl font-bold text-[#1e293b] mb-6">Rechenbeispiele für Itzehoe</h3>
              
              <div className="space-y-6">
                {/* Beispiel 1 */}
                <div className="border-l-4 border-[#1A52D8] pl-6">
                  <h4 className="font-bold text-[#1e293b] mb-2">Beispiel 1: Frau M., Pflegegrad 2</h4>
                  <div className="text-[#64748b] space-y-1">
                    <p>Benötigt: 15 Stunden Grundpflege/Monat</p>
                    <p>Kosten: 15h × 45€ = <strong className="text-[#1e293b]">675€</strong></p>
                    <p>Pflegekasse zahlt: 761€</p>
                    <p className="text-[#23D3D3] font-semibold">→ Eigenanteil: 0€ (sogar 86€ Reserve!)</p>
                  </div>
                </div>

                {/* Beispiel 2 */}
                <div className="border-l-4 border-[#23D3D3] pl-6">
                  <h4 className="font-bold text-[#1e293b] mb-2">Beispiel 2: Herr K., Pflegegrad 3</h4>
                  <div className="text-[#64748b] space-y-1">
                    <p>Benötigt: 30 Stunden Pflege/Monat (gemischt)</p>
                    <p>Kosten: 20h Grundpflege (900€) + 10h Behandlung (550€) = <strong className="text-[#1e293b]">1.450€</strong></p>
                    <p>Pflegekasse zahlt: 1.432€</p>
                    <p className="text-[#1e293b] font-semibold">→ Eigenanteil: 18€/Monat</p>
                  </div>
                </div>

                {/* Beispiel 3 */}
                <div className="border-l-4 border-[#1A52D8] pl-6">
                  <h4 className="font-bold text-[#1e293b] mb-2">Beispiel 3: Frau S., Pflegegrad 4 mit Demenz</h4>
                  <div className="text-[#64748b] space-y-1">
                    <p>Benötigt: 35 Stunden Demenzbetreuung/Monat</p>
                    <p>Kosten: 35h × 50€ = <strong className="text-[#1e293b]">1.750€</strong></p>
                    <p>Pflegekasse zahlt: 1.778€</p>
                    <p className="text-[#23D3D3] font-semibold">→ Eigenanteil: 0€</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zusatzleistungen */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8">
              Zusätzliche Leistungen ohne Mehrkosten
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <CheckCircle2 className="w-8 h-8 text-[#23D3D3] mb-3" />
                <h3 className="font-bold text-[#1e293b] mb-2">Kostenlose Erstberatung</h3>
                <p className="text-[#64748b]">
                  Unverbindliches Beratungsgespräch bei Ihnen zuhause. Wir klären Ihren Pflegebedarf und Ihre Ansprüche.
                </p>
              </div>

              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <CheckCircle2 className="w-8 h-8 text-[#23D3D3] mb-3" />
                <h3 className="font-bold text-[#1e293b] mb-2">Hilfe bei Pflegegrad-Antrag</h3>
                <p className="text-[#64748b]">
                  Wir unterstützen Sie kostenlos bei der Beantragung und Höherstufung des Pflegegrades.
                </p>
              </div>

              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <CheckCircle2 className="w-8 h-8 text-[#23D3D3] mb-3" />
                <h3 className="font-bold text-[#1e293b] mb-2">Pflegekassen-Abrechnung</h3>
                <p className="text-[#64748b]">
                  Wir rechnen direkt mit Ihrer Pflegekasse ab. Sie müssen nicht in Vorkasse gehen.
                </p>
              </div>

              <div className="bg-[#f8fafb] rounded-xl p-6 border border-[#e2e8f0]">
                <CheckCircle2 className="w-8 h-8 text-[#23D3D3] mb-3" />
                <h3 className="font-bold text-[#1e293b] mb-2">Keine versteckten Kosten</h3>
                <p className="text-[#64748b]">
                  Komplette Transparenz. Keine Anfahrtspauschalen, keine Wochenend-Zuschläge.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-8 text-center">
              Häufige Fragen zu Kosten
            </h2>

            <div className="space-y-4">
              {[
                {
                  q: "Muss ich die Pflege selbst bezahlen?",
                  a: "Nein, in den meisten Fällen übernimmt die Pflegekasse die Kosten vollständig. Nur wenn Ihr Pflegebedarf die monatliche Sachleistung übersteigt, zahlen Sie den Differenzbetrag selbst."
                },
                {
                  q: "Was kostet es, wenn ich keinen Pflegegrad habe?",
                  a: "Ohne Pflegegrad zahlen Sie die Pflege zunächst privat (45-55€/Stunde). Wir helfen Ihnen aber kostenlos, einen Pflegegrad zu beantragen. Rückwirkend ab Antragstellung werden die Kosten dann erstattet."
                },
                {
                  q: "Gibt es eine Mindestvertragslaufzeit?",
                  a: "Nein, Sie können unseren Service jederzeit ohne Kündigungsfrist beenden. Wir berechnen nur tatsächlich geleistete Pflegestunden."
                },
                {
                  q: "Wer legt die Preise fest?",
                  a: "Die Preise werden zwischen Pflegediensten und Pflegekassen vereinbart und sind in Schleswig-Holstein relativ einheitlich. Unsere Preise entsprechen dem regionalen Durchschnitt."
                },
                {
                  q: "Kann ich Pflege und Pflegegeld kombinieren?",
                  a: "Ja, das ist möglich (Kombinationsleistung). Wenn Sie z.B. nur 50% der Sachleistung nutzen, erhalten Sie 50% des Pflegegeldes zusätzlich. Wir beraten Sie gerne dazu."
                }
              ].map((faq, i) => (
                <details key={i} className="bg-white rounded-xl p-6 border border-[#e2e8f0]">
                  <summary className="font-semibold text-[#1e293b] cursor-pointer hover:text-[#1A52D8] transition-colors">
                    {faq.q}
                  </summary>
                  <p className="mt-4 text-[#64748b] leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white">
          <div className="container-custom text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Kostenlose Beratung zu Ihren Pflegekosten
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Wir klären gemeinsam, welche Leistungen Sie benötigen und was die Pflegekasse übernimmt. 
              Unverbindlich und kostenlos.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt beraten lassen: 0151 46173685
              </Button>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
